// A2-234 (G8) — plain wording for the form checks that had none.
//
// 85 of the 117 checks the twelve returns run had no entry in
// ruleCopy.js, so a failure showed the check's internal description
// ("output_vat_total = 1a_btw + 1b_btw + ..."). Merged into RULE_COPY
// there; kept here because ruleCopy.js is at the 500-line cap.
// tests/test_a2_234_rule_copy_complete.py fails on any check without
// wording. A name used by several returns (deduction_non_negative) is
// worded to hold for each of them.

const SUMS = "The app computes these boxes from each other, so if you typed "
  + "nothing into them this is an app error, not your data. Don't file — "
  + "report it with this calculation.";
const TYPED = "Check the figures you typed into these boxes; if you typed none, "
  + "it is an app error — don't file, report it.";
const ASK = "You can still file once you have checked it.";

const sum = (what) => ({ headline: `The return's totals don't add up: ${what}.`, fix: SUMS });
const typed = (what) => ({ headline: `${what} doesn't add up.`, fix: TYPED });

export const RULE_COPY_FORMS = {
  // ── France (CA3) ──
  period_is_not_an_annual_filing: {
    headline: "A twelve-month period isn't a CA3.",
    fix: "The annual return is the CA12 (3517-S-SD), which this app doesn't produce. Pick a month or quarter.",
  },
  tva_brute_non_negative: {
    headline: "A French output-VAT box is negative, and the CA3 never takes negative amounts.",
    fix: "A credit note larger than the period's sales belongs on line 21 (regularisation), not netted into the sales line.",
  },
  box_16_sum: sum("line 16 against the output-VAT lines"),
  oss_split_is_exhaustive: sum("lines E2 and E3 against the OSS total"),
  fr_rate_line_bases_match_cadre_a: typed("Lines A1–B4 against the bases of the rate lines (DGFiP control B688)"),
  fr_deduction_lines_not_negative: {
    headline: "A French deduction line (19, 20, 21, 22 or 26) is negative.",
    fix: "The DGFiP refuses negatives there (control FN15). Purchase credit notes larger than the period's deductions are reported as VAT to repay, not as a negative deduction — check them.",
  },
  box_23_sum: sum("line 23 against the deduction lines"),
  import_vat_within_deduction: typed("Line 24 (import VAT) against lines 19 and 20 that contain it"),
  credit_xor_due: sum("both a credit (line 25) and VAT due (TD) are filled"),
  box_28_matches_TD: sum("line 28 against TD"),
  e2_residual_non_negative: {
    headline: "Line E2 is negative.",
    fix: "Credit notes on zero-rated or exempt sales exceed the period's sales. Check them; the excess belongs in the period of the original sales.",
  },
  refund_request_within_threshold: {
    headline: "The refund asked for on line 26 isn't allowed.",
    fix: "It must be nothing, or between EUR 760 (EUR 150 in December or Q4) and the credit on line 25. Change line 26.",
  },
  // ── Germany (UStVA) ──
  kz83_payable_matches: sum("Kz 83 against output minus input VAT"),
  kz66_non_negative: {
    headline: "Kz 66 (input VAT) is negative.",
    fix: `Purchase credit notes are netted into it, which the form allows. ${ASK}`,
  },
  // ── Spain (modelo 303) ──
  casilla_27_total_output_vat: sum("casilla 27 against the output cuotas"),
  casilla_45_total_input_vat: sum("casilla 45 against the deductible cuotas"),
  casilla_46_equals_27_minus_45: sum("casilla 46 against 27 minus 45"),
  casilla_71_formula: typed("Casilla 71 against 69 − 70 + 109"),
  casilla_65_foral_pct_in_range: {
    headline: "Casilla 65 must be a percentage from 0 to 100.",
    fix: "Enter the share of the result attributable to the State (100 unless the client also files in País Vasco or Navarra).",
  },
  deduction_casillas_non_negative: {
    headline: "A Spanish deduction casilla (28–39) is negative.",
    fix: "AEAT accepts only positive figures there. A purchase credit note belongs in casilla 41.",
  },
  deduccion_non_negative: {
    headline: "Casilla 45 (total to deduct) is negative.",
    fix: `AEAT accepts a negative total. ${ASK}`,
  },
  exon_390_answered_on_the_last_return: {
    headline: "The last return of the year must say whether the client is exempt from modelo 390.",
    fix: "Answer the modelo 390 question (1 exempt, 2 not exempt) on this December or 4T return.",
  },
  pagina4_filled_when_exempt: {
    headline: "An exempt filer's last return needs the year's turnover on página 4.",
    fix: "Fill casillas 80–99 (total in 88) with the year's turnover.",
  },
  refund_solicitada_valid: {
    headline: "The refund asked for in casilla 73 isn't allowed.",
    fix: "It must be nothing or the whole balance (the negative result plus credit brought forward, less what is offset) — never part of it.",
  },
  // ── United Kingdom (VAT return) ──
  box3_equals_1_plus_2: sum("Box 3 against Boxes 1 and 2"),
  box5_equals_abs_3_minus_4: sum("Box 5 against Boxes 3 and 4"),
  box5_signed_equals_3_minus_4: sum("the net VAT against Boxes 3 and 4"),
  box4_within_hmrc_range: {
    headline: "Box 4 is outside the range HMRC's system accepts.",
    fix: "Check the typed purchase figures — an amount this size is almost certainly a keying slip.",
  },
  // ── Italy (LIPE) ──
  vp6_col1_formula: sum("VP6 column 1 against VP4 and VP5"),
  vp6_col2_formula: sum("VP6 column 2 against VP4 and VP5"),
  vp6_xor: sum("both columns of VP6 are filled"),
  vp12_quarterly_interest_formula: typed("The VP12 quarterly interest"),
  vp14_formula: typed("VP14 against the period's debits and credits"),
  q4_suppression: {
    headline: "This fourth quarter settles in the annual return.",
    fix: "VP11, VP12 and VP14 must be empty for an Art. 7 fourth quarter. Clear them.",
  },
  vp7_max_100_eur: {
    headline: "VP7 can only carry a debit of EUR 100 or less.",
    fix: "A larger debit from the previous period should have been paid then. Correct VP7.",
  },
  positive_only_boxes: {
    headline: "VP7, VP8, VP10, VP11, VP12 and VP13 take positive amounts only.",
    fix: "Enter the amount without a minus sign.",
  },
  vp13_threshold: {
    headline: "VP13 (acconto) under EUR 103.29 isn't due.",
    fix: "Leave VP13 empty when the advance is below EUR 103.29.",
  },
  vp13_method_valid: {
    headline: "VP13 needs the method used for the advance.",
    fix: "Pick method 1, 2, 3 or 4 for the acconto.",
  },
  deduction_non_negative: {
    headline: "The deductible input-VAT box is negative.",
    fix: "It usually comes from purchase credit notes. Italy, Czechia and Hungary accept it — confirm it. Portugal does not: the credit note is reported as a regularisation instead — check it.",
  },
  vp7_absent_in_forbidden_period: {
    headline: "VP7 must be empty in this period.",
    fix: "No previous-period debit carries into Q1, Q4, an Art. 7 fourth quarter, January or December. Clear VP7.",
  },
  vp8_absent_in_first_period: {
    headline: "VP8 must be empty in the first period of the year.",
    fix: "A credit from last year goes in VP9, not VP8.",
  },
  vp9_not_negative_in_first_period: {
    headline: "VP9 can't be negative in the first period.",
    fix: "Enter last year's credit as a positive amount.",
  },
  vp11_within_authority_cap: {
    headline: "VP11 (tax credits used) is more than the period's debit allows.",
    fix: "Reduce VP11 to no more than VP6 col. 1 + VP7 − VP8 − VP9 − VP10.",
  },
  vp13_only_in_final_period: {
    headline: "The advance (VP13) is reported only in Q4 or December.",
    fix: "Clear VP13 for this period.",
  },
  // ── Denmark (momsangivelse) ──
  exempt_activities_question_in_the_june_period: {
    headline: "Denmark's yearly question about VAT-exempt activities is due on this return.",
    fix: "Answer it (and give the provisional deduction percentage if the answer is yes) on the first return covering June.",
  },
  momstilsvar_matches: sum("momstilsvar against salgsmoms, acquisition VAT and købsmoms"),
  kobsmoms_non_negative: {
    headline: "Købsmoms (input VAT) is negative.",
    fix: `Denmark accepts it; it usually comes from purchase credit notes. ${ASK}`,
  },
  // ── Austria (U30) ──
  zahllast_matches: sum("KZ 095 against output VAT and the deductions"),
  gesamt_base_matches: sum("KZ 000 against the turnover lines"),
  vorsteuer_non_negative: {
    headline: "KZ 060 (input VAT) is negative.",
    fix: "Austria refuses it. Purchase credit notes reducing earlier deductions belong in KZ 090.",
  },
  base_kennzahlen_non_negative: {
    headline: "An Austrian turnover line is negative.",
    fix: "Austria accepts negatives only in KZ 063, 067 and 090. Check the credit notes — the excess belongs in the period of the original sales.",
  },
  kz015_zero_rate_only_before_2026: {
    headline: "KZ 015 (domestic zero rate) no longer exists for 2026 periods.",
    fix: "The photovoltaic zero rate was removed from the 2026 U30. Check the rate on these sales.",
  },
  // ── Czechia (přiznání k DPH) ──
  output_tax_matches: sum("total output VAT against its rows"),
  net_tax_matches: sum("the net tax against output minus input VAT"),
  deduction_includes_full_deduction_lines: {
    headline: "Row 63 (total deduction) is smaller than the rows it should include.",
    fix: `Row 63 normally equals rows 46 + 52 + 53 + 60. Check the typed figures. ${ASK}`,
  },
  self_assessed_output_non_negative: {
    headline: "VAT you self-assessed on purchases (rows 3–13) is negative.",
    fix: `The Czech return accepts it; it comes from credit notes on those purchases. ${ASK}`,
  },
  // ── Portugal (declaração periódica) ──
  imposto_liquidado_matches: sum("the output VAT against the regional fields"),
  apuramento_matches: typed("Campos 93/94 against the VAT due and campo 24"),
  rate_base_below_zero_advisory: {
    headline: "A Portuguese rate line's base is below zero.",
    fix: `A credit note netted here may belong in campo 40 (regularisation of earlier periods). ${ASK}`,
  },
  pt_region_undetermined: {
    headline: "Some Portuguese sales have no region (mainland, Azores or Madeira).",
    fix: "Fill the PT Region column for these rows and run the calculation again.",
  },
  pt_region_rate_validity: {
    headline: "A rate doesn't exist in the region its sales are booked to.",
    fix: "Check the PT Region and the rate on these rows — Azores and Madeira rates differ from the mainland's.",
  },
  pt_unknown_rate: {
    headline: "A Portuguese rate isn't in this period's rate table.",
    fix: "Check the rate on these rows; an old or mistyped rate can't be filed.",
  },
  refund_request_within_credit: {
    headline: "The refund asked for in campo 95 is more than the credit.",
    fix: "Lower campo 95 to no more than the credit in campo 94.",
  },
  pt_vat_matches_base_times_rate: {
    headline: "A rate line's VAT is more than EUR 4.99 away from its base times the rate.",
    fix: `Check the VAT on the invoices in that line. ${ASK}`,
  },
  pt_refund_needs_client_and_supplier_lists: {
    headline: "A refund (campo 95) needs the client and supplier lists filed with it.",
    fix: `Make sure both lists go in on time with this return. ${ASK}`,
  },
  pt_campos_40_61_not_negative: {
    headline: "Campo 40 or 61 is negative.",
    fix: "Enter the regularisation as a positive amount in the field for its direction.",
  },
  // ── Hungary (65A) ──
  row_76_includes_rows_63_75: {
    headline: "Row 76 is smaller than rows 63–75 and 111 it should total.",
    fix: `Check the typed deduction rows. ${ASK}`,
  },
  huf_thousand_forint: {
    headline: "Hungarian figures must be in whole thousands of forints.",
    fix: "Round each typed figure to the nearest thousand forints.",
  },
  hu_pay_xor_reclaim: sum("both row 84 (to pay) and row 85 (to reclaim) are filled"),
  hu_reclaim_within_the_credit: {
    headline: "Row 85 reclaims more than the credit on row 83.",
    fix: "Lower row 85 to no more than the credit.",
  },
  hu_credit_fully_accounted: {
    headline: "Part of the credit on row 83 is neither reclaimed nor carried forward.",
    fix: "Row 85 plus row 86 must equal the credit.",
  },
  hu_reclaim_all_or_nothing: {
    headline: "Rows 85 and 86 can't both be filled.",
    fix: "Reclaim the whole credit (row 85) or carry all of it forward (row 86).",
  },
  hu_rows_82_85_not_negative: {
    headline: "Row 82 or 85 is negative.",
    fix: "NAV refuses negatives there (rule M032). Enter the amount without a minus sign.",
  },
  hu_reclaim_below_the_refund_minimum: {
    headline: "The reclaim on row 85 is below NAV's minimum for this filing frequency.",
    fix: `Carry the credit forward on row 86 instead, unless the client qualifies otherwise. ${ASK}`,
  },
  hu_rate_rows_within_nav_tolerance: {
    headline: "A Hungarian rate row's tax isn't its base times the rate.",
    fix: "NAV allows less than 1 thousand forints of difference. Check the VAT on the invoices in that row.",
  },
  // ── Netherlands (btw-aangifte) ──
  output_vat_total_matches_components: sum("the output VAT total against its rubrieken"),
  total_signed_matches_formula: sum("the amount to pay or reclaim against output VAT and 5b"),
  voorbelasting_non_negative: {
    headline: "Rubriek 5b (voorbelasting) is negative.",
    fix: `The Dutch return accepts it; it usually comes from purchase credit notes. ${ASK}`,
  },
  // ── Sweden (momsdeklaration) ──
  ruta_48_non_negative: {
    headline: "Ruta 48 (input VAT) is negative.",
    fix: `Skatteverket accepts it; it usually comes from purchase credit notes. ${ASK}`,
  },
  self_assessed_vat_also_reclaimed: {
    headline: "VAT you self-assessed on purchases is larger than ruta 48.",
    fix: `It is normally reclaimed in the same period through ruta 48 — check it. ${ASK}`,
  },
};
